import throttle from 'lodash/throttle';
import { useIdleTimerContextSafe } from 'providers/IdleTimerContext';
import { useCallback, useRef } from 'react';
import { MEDIA_PLAYING_TIMESTAMP_KEY } from '../constants/inactivityTimer';

/**
 * Hook to track media playback (video/audio) for inactivity timer purposes.
 * Uses a timestamp-only approach: updates timestamp on tick events, and checks
 * if timestamp is fresh to determine if media is playing.
 * Only tracks if inactivity tracking is enabled. Returns no-op functions if disabled.
 *
 * Throttles localStorage updates to at most once per second to avoid excessive writes.
 */
export const useMediaInactivityTracking = () => {
  const idleTimerContext = useIdleTimerContextSafe();
  const isInactivityTrackingEnabled = idleTimerContext?.isEnabled() ?? false;

  // Throttle localStorage updates to at most once per second
  // This prevents excessive writes if tick events fire more frequently than expected
  const throttledUpdate = useRef(
    throttle(
      () => {
        localStorage.setItem(
          MEDIA_PLAYING_TIMESTAMP_KEY,
          Date.now().toString(),
        );
      },
      1000,
      { leading: true, trailing: false },
    ),
  ).current;

  const updateMediaTimestamp = useCallback(() => {
    if (!isInactivityTrackingEnabled) {
      return;
    }
    throttledUpdate();
  }, [isInactivityTrackingEnabled, throttledUpdate]);

  const clearMediaTimestamp = useCallback(() => {
    if (!isInactivityTrackingEnabled) {
      return;
    }
    localStorage.removeItem(MEDIA_PLAYING_TIMESTAMP_KEY);
  }, [isInactivityTrackingEnabled]);

  return {
    updateMediaTimestamp,
    clearMediaTimestamp,
    isEnabled: isInactivityTrackingEnabled,
  };
};
